/** Common */

export type EnhancedMentionToken = {
  id: number | null;
  name: string;
};

export type EnhancedContentToken =
  | { type: 'text'; value: string }
  | { type: 'mention'; value: EnhancedMentionToken };

/** DTOs */

export interface CommentDTO {
  id: number;
  content_json_enhanced: EnhancedContentToken[];
  created_epoch_time: number;
  user_id: number | null;
  user_name: string | null;
  user_picture_url: string | null;
}

export interface LabelDTO {
  id: number;
  icon: string;
  name: string;
  highlight_id: number;
  custom?: boolean;
  key_name: string | null;
}

export type CommentsByRecordingIdDTO = {
  [recordingId: number]: CommentDTO[];
};

export type LabelsByRecordingIdDTO = {
  [recordingId: number]: LabelDTO[];
};

/** Models */

export interface Comment {
  id: number;
  contentJsonEnhanced: EnhancedContentToken[];
  createdEpochTime: number;
  userId: number | null;
  userName: string | null;
  userPictureUrl: string | null;
}

export interface AppliedLabel {
  id: number;
  icon: string;
  name: string;
  highlightId: number;
  custom?: boolean;
  keyName: string | null;
}

export type CommentsByRecordingId = {
  [recordingId: number]: Comment[];
};

export type LabelsByRecordingId = {
  [recordingId: number]: AppliedLabel[];
};

/** Mappers */

export function commentsByRecordingIdDtoToModel(
  commentsByRecordingIdDto: CommentsByRecordingIdDTO,
): CommentsByRecordingId {
  return Object.fromEntries(
    Object.entries(commentsByRecordingIdDto).map(([recordingId, commentDtos]) => [
      Number(recordingId),
      commentDtos
        .map((commentDto) => ({
          id: commentDto.id,
          contentJsonEnhanced: commentDto.content_json_enhanced,
          createdEpochTime: commentDto.created_epoch_time,
          userId: commentDto.user_id,
          userName: commentDto.user_name,
          userPictureUrl: commentDto.user_picture_url,
        }))
        .sort((comment1: Comment, comment2: Comment) => {
          return comment1.createdEpochTime - comment2.createdEpochTime;
        }),
    ]),
  );
}

export function labelsByRecordingIdDtoToModel(
  labelsByRecordingIdDto: LabelsByRecordingIdDTO,
): LabelsByRecordingId {
  return Object.fromEntries(
    Object.entries(labelsByRecordingIdDto).map(([recordingId, labelDtos]) => [
      Number(recordingId),
      labelDtos.map((labelDto) => ({
        id: labelDto.id,
        icon: labelDto.icon,
        name: labelDto.name,
        highlightId: labelDto.highlight_id,
        keyName: labelDto.key_name ?? null,
        custom: labelDto.custom,
      })),
    ]),
  );
}
